"use client";

import { useEffect, useRef } from "react";
import { useFocusState, getRemainingMs, formatClock } from "@/components/focus/useFocusState";

const ICON = "/icons/icon-192.png";

// Powiadomienie pokazujemy tylko, gdy karta jest w tle (użytkownik przełączył się
// gdzie indziej) — inaczej wystarcza interfejs w aplikacji. Na desktopie działa
// konstruktor Notification; część przeglądarek mobilnych go nie wspiera (try/catch).
function sendNotification(title: string, body: string) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  if (typeof document !== "undefined" && !document.hidden) return;
  try {
    const n = new Notification(title, { body, icon: ICON, tag: "ns-focus" });
    window.setTimeout(() => n.close(), 8000);
  } catch {
    /* brak wsparcia bez service workera */
  }
}

/**
 * Efekty „poza drzewem": dynamiczne odliczanie w tytule karty przeglądarki
 * (skupienie i przerwa) oraz powiadomienia systemowe przy zmianach faz.
 * Montowane raz w FocusTimerLayer. Zwraca null.
 */
export default function FocusPageEffects() {
  const state = useFocusState();
  const mode = state.mode;
  const remaining = getRemainingMs(state);

  // Odliczanie w tytule karty przeglądarki.
  const baseTitleRef = useRef<string | null>(null);
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (mode === "idle") {
      if (baseTitleRef.current !== null) {
        document.title = baseTitleRef.current;
        baseTitleRef.current = null;
      }
      return;
    }
    if (baseTitleRef.current === null) baseTitleRef.current = document.title;
    const clock = formatClock(remaining);
    document.title = mode === "break" ? `☕ ${clock} · Przerwa` : `⏳ ${clock} · Skupienie`;
  }, [mode, remaining]);

  // Powiadomienia — raz na fazę (klucz = id sesji + faza).
  const notifiedRef = useRef<Record<string, boolean>>({});
  const notifyOnce = (key: string, title: string, body: string) => {
    if (notifiedRef.current[key]) return;
    notifiedRef.current[key] = true;
    sendNotification(title, body);
  };

  useEffect(() => {
    if (mode === "focus" && state.preBreakActive && state.autoBreaks) {
      notifyOnce(`${state.id}:pre`, "Zbliża się przerwa", "Zostało Ci około 5 minut skupienia.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, state.preBreakActive, state.autoBreaks, state.id]);

  useEffect(() => {
    if (mode === "focus" && state.awaitingBreakChoice && state.autoBreaks) {
      notifyOnce(`${state.id}:end`, "Czas na przerwę ☕", "Zaplanowany czas skupienia dobiegł końca.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, state.awaitingBreakChoice, state.autoBreaks, state.id]);

  useEffect(() => {
    if (mode === "break" && state.breakDone) {
      notifyOnce(`${state.id}:breakend`, "Przerwa zakończona ✓", "Gotowy na kolejną sesję? Wracamy do nauki.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, state.breakDone, state.id]);

  // Wyjście z pełnego ekranu, gdy zaczyna się przerwa lub pojawia się propozycja
  // przerwy. Wideo lekcji w trybie pełnoekranowym (nasz kontener albo natywny
  // iframe Bunny) renderuje się na warstwie wierzchniej i zasłoniłoby ekran/okno
  // przerwy — bez tego uczeń zobaczyłby je dopiero po ręcznym wyjściu z pełnego
  // ekranu. Robimy to raz na fazę (klucz = id sesji + faza).
  const breakActive = mode === "break";
  const choiceActive = mode === "focus" && state.awaitingBreakChoice && state.autoBreaks;
  const fsExitedRef = useRef("");
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!breakActive && !choiceActive) return;
    const key = `${state.id}:${breakActive ? "break" : "choice"}`;
    if (fsExitedRef.current === key) return;
    fsExitedRef.current = key;
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakActive, choiceActive, state.id]);

  return null;
}
